import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTemplatesForType, getBestMatchTemplate } from '@/lib/celebrations/celebrationTemplateMatching';
import { useCelebrationTemplates } from '@/hooks/useCelebrationTemplates';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, ListChecks, Tags, Sparkles, Eye, X, ArrowRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CelebrationDraftDto, CelebrationTemplateData } from '@om/shared-types';

interface TemplatePickerProps {
  celebrationType: string;
  celebrationSubtype: string | null;
  selectedTemplateKey: string | null;
  onSelect: (templateKey: string | null) => void;
  preferenceContext?: {
    confirmedTags?: Record<string, string[]>;
    extractedFields?: CelebrationDraftDto;
    suggestedTemplateKeys?: string[];
  };
}

export const TemplatePicker: React.FC<TemplatePickerProps> = ({
  celebrationType,
  celebrationSubtype,
  selectedTemplateKey,
  onSelect,
  preferenceContext,
}) => {
  const { t } = useTranslation('celebrations');
  const [previewTemplate, setPreviewTemplate] = useState<CelebrationTemplateData | null>(null);
  const { templates: dbTemplates, loading } = useCelebrationTemplates();
  const allTemplates = getTemplatesForType(dbTemplates, celebrationType);
  const bestMatch = getBestMatchTemplate(dbTemplates, celebrationType, celebrationSubtype);
  const hasPreferenceContext = Boolean(preferenceContext);

  const scoreTemplateByPreferences = (template: CelebrationTemplateData): number => {
    const confirmedTags = preferenceContext?.confirmedTags ?? {};
    const hasConfirmedTags = Object.values(confirmedTags).some(values => values.length > 0);
    if (!hasConfirmedTags) return 0;

    let score = 0;

    for (const [category, selectedValues] of Object.entries(confirmedTags)) {
      const templateValues = template.suggestedTags[category];
      if (!templateValues || templateValues.length === 0) continue;

      score += 2;
      const templateValueSet = new Set(templateValues.map(v => v.toLowerCase()));
      for (const value of selectedValues) {
        if (templateValueSet.has(value.toLowerCase())) {
          score += 5;
        }
      }
    }

    return score;
  };

  const scoredTemplates = allTemplates.map(template => ({
    template,
    score: scoreTemplateByPreferences(template),
  }));

  const sortedTemplates = scoredTemplates
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.template.key === bestMatch?.key) return -1;
      if (b.template.key === bestMatch?.key) return 1;
      if (a.template.subtype && !b.template.subtype) return -1;
      if (!a.template.subtype && b.template.subtype) return 1;
      return 0;
    })
    .map(item => item.template);

  const isBlank = selectedTemplateKey === null;
  const recommendedTemplateKey = sortedTemplates[0]?.key ?? bestMatch?.key;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('templatePicker.description')}
      </p>

      {hasPreferenceContext && (
        <p className="text-xs text-muted-foreground">
          {t('templatePicker.preferenceHint')}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Blank / Scratch card (always available, also when no templates exist) */}
        <TemplateCard
          isBlank
          selected={isBlank}
          onSelect={() => onSelect(null)}
        />

        {loading && (
          <p className="text-sm text-muted-foreground self-center">{t('templatePicker.loadingTemplates')}</p>
        )}

        {/* Template cards */}
        {sortedTemplates.map((tpl) => (
          <TemplateCard
            key={tpl.key}
            template={tpl}
            selected={selectedTemplateKey === tpl.key}
            recommended={tpl.key === recommendedTemplateKey}
            onSelect={() => onSelect(tpl.key)}
            onPreview={() => setPreviewTemplate(tpl)}
          />
        ))}
      </div>

      {/* Preview Dialog */}
      {previewTemplate && (
        <TemplatePreviewDialog
          template={previewTemplate}
          isSelected={selectedTemplateKey === previewTemplate.key}
          onClose={() => setPreviewTemplate(null)}
          onSelect={() => {
            onSelect(previewTemplate.key);
            setPreviewTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TemplateCard
// ---------------------------------------------------------------------------

interface TemplateCardProps {
  template?: CelebrationTemplateData;
  isBlank?: boolean;
  selected: boolean;
  recommended?: boolean;
  onSelect: () => void;
  onPreview?: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isBlank,
  selected,
  recommended,
  onSelect,
  onPreview,
}) => {
  const { t } = useTranslation('celebrations');
  const eventCount = template?.suggestedEvents.length ?? 0;
  const taskCount = template?.suggestedTasks.length ?? 0;
  const tagCategories = template ? Object.keys(template.suggestedTags) : [];

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group relative flex flex-col items-start gap-3 rounded-xl border-2 p-4 text-left transition-all',
        'hover:shadow-md hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
        selected
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border bg-card',
      )}
    >
      {/* Recommended badge */}
      {recommended && !selected && (
        <Badge variant="secondary" className="absolute -top-2.5 right-3 text-xs gap-1">
          <Sparkles className="h-3 w-3" /> {t('templatePicker.bestMatch')}
        </Badge>
      )}

      {/* Selected checkmark */}
      {selected && (
        <div className="absolute -top-2.5 right-3 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow">
          ✓
        </div>
      )}

      {/* Title row with preview button */}
      <div className="flex items-start justify-between w-full gap-2">
        <h4 className="text-base font-semibold leading-tight">
          {isBlank ? t('templatePicker.startFromScratch') : template!.label}
        </h4>
        {!isBlank && onPreview && (
          <span
            role="button"
            tabIndex={0}
            title={t('templatePicker.previewTitle')}
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onPreview(); } }}
            className="shrink-0 p-1 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
          >
            <Eye className="h-4 w-4" />
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground line-clamp-2">
        {isBlank
          ? t('templatePicker.startFromScratchDescription')
          : template!.description}
      </p>

      {/* Stats row */}
      {!isBlank && (
        <div className="flex flex-wrap gap-3 mt-auto pt-2 text-xs text-muted-foreground">
          {eventCount > 0 && (
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              {t('templatePicker.eventsCount', { count: eventCount })}
            </span>
          )}
          {taskCount > 0 && (
            <span className="flex items-center gap-1">
              <ListChecks className="h-3.5 w-3.5" />
              {t('templatePicker.tasksCount', { count: taskCount })}
            </span>
          )}
          {tagCategories.length > 0 && (
            <span className="flex items-center gap-1">
              <Tags className="h-3.5 w-3.5" />
              {t('templatePicker.tagGroupsCount', { count: tagCategories.length })}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// TemplatePreviewDialog
// ---------------------------------------------------------------------------

const PRIORITY_COLORS: Record<string, string> = {
  high: 'text-red-600 bg-red-50',
  medium: 'text-amber-600 bg-amber-50',
  low: 'text-slate-600 bg-slate-50',
};

interface TemplatePreviewDialogProps {
  template: CelebrationTemplateData;
  isSelected: boolean;
  onClose: () => void;
  onSelect: () => void;
}

const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({
  template,
  isSelected,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation('celebrations');
  const events = template.suggestedEvents;
  const tasks = template.suggestedTasks;
  const tagEntries = Object.entries(template.suggestedTags);

  // Group tasks by event order
  const tasksByEvent = tasks.reduce<Record<number, typeof tasks>>((acc, t) => {
    (acc[t.eventOrder] ??= []).push(t);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 pb-3 border-b">
          <div>
            <h2 className="text-lg font-semibold leading-6 text-gray-900">{template.label}</h2>
            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
          </div>
          <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label={t('templatePicker.closePreviewAria')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {/* Events Section */}
            {events.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" />
                  {t('templatePicker.preview.eventsTitle', { count: events.length })}
                </h3>
                <div className="space-y-3">
                  {events.map((ev, idx) => {
                    const eventTasks = tasksByEvent[ev.order] ?? [];
                    return (
                      <div key={idx} className="rounded-lg border bg-card p-3">
                        <div className="flex items-center gap-2">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                            {ev.order}
                          </span>
                          <span className="font-medium text-sm">{ev.title}</span>
                          <Badge variant="outline" className="text-[10px] ml-auto">
                            {ev.event_type.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-8">{ev.description}</p>

                        {/* Tasks for this event */}
                        {eventTasks.length > 0 && (
                          <div className="mt-2 ml-8 space-y-1">
                            {eventTasks.map((task, ti) => (
                              <div key={ti} className="flex items-center gap-2 text-xs">
                                <CheckCircle2 className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                                <span className="text-foreground/80">{task.title}</span>
                                <span className={cn('ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium capitalize', PRIORITY_COLORS[task.priority])}>
                                  {task.priority}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Celebration-level tasks (eventOrder === 0) */}
                {(tasksByEvent[0]?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">{t('templatePicker.preview.generalTasks')}</p>
                    <div className="space-y-1">
                      {tasksByEvent[0].map((task, ti) => (
                        <div key={ti} className="flex items-center gap-2 text-xs">
                          <CheckCircle2 className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                          <span className="text-foreground/80">{task.title}</span>
                          <span className={cn('ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium capitalize', PRIORITY_COLORS[task.priority])}>
                            {task.priority}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Tags Section */}
            {tagEntries.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                  <Tags className="h-4 w-4" />
                  {t('templatePicker.preview.suggestedTags')}
                </h3>
                <div className="space-y-2">
                  {tagEntries.map(([category, values]) => (
                    <div key={category} className="flex items-start gap-2">
                      <span className="text-xs font-medium text-foreground/70 min-w-[100px] pt-0.5">{category}</span>
                      <div className="flex flex-wrap gap-1">
                        {values.length > 0 ? (
                          values.map((v) => (
                            <Badge key={v} variant="secondary" className="text-xs">
                              {v}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground italic">{t('templatePicker.preview.aiSuggestsValues')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-muted/30">
          <p className="text-xs text-muted-foreground">
            {t('templatePicker.preview.footerSummary', {
              eventsCount: events.length,
              tasksCount: tasks.length,
              tagGroupsCount: tagEntries.length,
            })}
          </p>
          {isSelected ? (
            <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
              <CheckCircle2 className="h-4 w-4" /> {t('templatePicker.selected')}
            </span>
          ) : (
            <button
              type="button"
              onClick={onSelect}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t('templatePicker.useTemplate')} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
